import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { IconButton, Alert, Snackbar } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import axios from 'axios';
import { Flag, Download, TestTubeDiagonal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import { Button as ShadcnButton } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '../../components/ui/tooltip';
import { CustomDrawer } from '../../components/CustomDrawer/CustomDrawer';
import formatDate from '../../utils/formatDate';
import AnalyticsService from '../../services/AnalyticsService/AnalyticsService';
import { ConversationResponse } from '../../types/ConversationResponse';
import { ConversationFilterParams } from '../../types/ConversationFilterParams';
import PageTitleSection from '../../components/PageTitleSection/PageTitleSection';
import ConversationsDateRangeContainer from '../../components/Analytics/ConversationsDateRangeContainer/ConversationsDateRangeContainer';
import { flagStyles } from './styles';
import ExportFileType from '../../types/ExportFileType';
import { browserTimezone } from '../../utils/dayjsSetup';
import SortDirection from '../../types/SortDirection';
import SortColumn from '../../types/SortColumn';
import ChatbotContext from '../../context/ChatbotContext/ChatbotContext';
import useDateRange from '../../hooks/useDateRange';
import useAbortController from '../../hooks/useAbortController';
import ConversationSidebar from '../ConversationSidebar/ConversationSidebar';
import { HeaderDefinition } from '../../components/DataTable/DataTableHeader';
import { TableCell } from '../../components/ui/table';
import TopicsContainer from '../../components/TopicsContainer/TopicsContainer';
import HealthScore from './HealthScore/HealthScore';
import SearchBar from '../../components/SearchBar/SearchBar';
import PageContentContainer from '../../components/PageContentContainer/PageContentContainer';
import ConversationActionsDropdown from '../../components/ConversationActionsDropdown/ConversationActionsDropdown';
import DataTable from '../../components/DataTable/DataTable';
import useAuthRoles from '../../hooks/useAuthRoles';
import AsyncSearchSelect from '../../components/AsyncSearchSelect/AsyncSearchSelect';
import Issue from '../../types/Issue';

function ConversationsPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userIsJapetoAdmin } = useAuthRoles();
  const isJapetoAdmin = userIsJapetoAdmin();

  const { dateRange, setDateRange, includeTest, setIncludeTest } =
    useDateRange();

  const [scrollY] = useState<number>(+searchParams.get('scrollY')! || 0);
  const [page, setPage] = useState<number>(+searchParams.get('page')! || 0);
  const initialSearchTerm =
    searchParams.get('search') || searchParams.get('conversationId') || '';
  const [query, setQuery] = useState<string>(initialSearchTerm);
  const [issueReference, setIssueReference] = useState<string>(
    searchParams.get('issueReference') || ''
  );
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(() => {
    const reference = searchParams.get('issueReference');
    return reference ? ({ reference } as Issue) : null;
  });

  const handleIssueFilterSelect = (issue: Issue | null): void => {
    setSelectedIssue(issue);
    setIssueReference(issue ? issue.reference : '');
  };
  const [filterConfig, setFilterConfig] = useState<ConversationFilterParams>(
    () => ({
      q: initialSearchTerm,
      firstMessageStartTime: dateRange[0].format('YYYY-MM-DDTHH:mm'),
      firstMessageEndTime: dateRange[1].format('YYYY-MM-DDTHH:mm'),
      includeTest,
      issueReference: searchParams.get('issueReference') || ''
    })
  );

  const [numberOfResults, setNumberOfResults] = useState<number>(0);
  const [searchData, setSearchData] = useState<ConversationResponse[]>();
  const [searchIsLoading, setSearchIsLoading] = useState<boolean>(true);
  const [resultsPerPage, setResultsPerPage] = useState<number>(25);
  const [hasError, setHasError] = useState<boolean>(false);

  const [sortColumn, setSortColumn] = React.useState<SortColumn>(
    (searchParams.get('sortColumn') as SortColumn) || 'firstMessageTime'
  );
  const [sortDirection, setSortDirection] = React.useState<SortDirection>(
    (searchParams.get('sortDirection') as SortDirection) || 'DESC'
  );

  const daysInRange = useMemo(
    () => (dateRange[1]?.diff(dateRange[0], 'day') || 0) + 1,
    [dateRange]
  );
  const { firstMessageStartTime, firstMessageEndTime } = filterConfig;

  function updateQueryParams(): void {
    setSearchParams((params) => {
      params.set('search', query);
      params.set('issueReference', issueReference ?? '');
      params.delete('conversationId');
      params.set('firstMessageStartTime', firstMessageStartTime as string);
      params.set('firstMessageEndTime', firstMessageEndTime as string);
      params.set('sortColumn', sortColumn);
      params.set('sortDirection', sortDirection ?? '');
      params.set('page', page.toString());
      params.set('scrollY', scrollY.toString());
      return params;
    });
  }

  useEffect(() => {
    const main = document.getElementsByTagName('main')[0];
    if (searchData !== undefined && searchData.length > 0 && main) {
      setTimeout(() => {
        main.scrollTo({
          top: scrollY
        });
      }, 10);
    }
  }, [searchData, scrollY]);

  const { currentChatbot } = React.useContext(ChatbotContext);

  const { getAbortSignal } = useAbortController();

  const timezoneParsedFilterConfig = useMemo(
    () => ({
      ...filterConfig,
      firstMessageStartTime: dayjs
        .utc(filterConfig.firstMessageStartTime)
        .tz(browserTimezone)
        .format('YYYY-MM-DDTHH:mm'),
      firstMessageEndTime: dayjs
        .utc(filterConfig.firstMessageEndTime)
        .tz(browserTimezone)
        .format('YYYY-MM-DDTHH:mm')
    }),
    [filterConfig]
  );

  const fetchConversations = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}): Promise<void> => {
      if (!currentChatbot) return;

      const signal = getAbortSignal();

      if (!silent) {
        setSearchIsLoading(true);
        setSearchData(undefined);
        setNumberOfResults(0);
      }

      try {
        const data = await AnalyticsService.getConversations({
          chatbotId: currentChatbot.id,
          filterConfig: timezoneParsedFilterConfig,
          sortDirection,
          sortColumn,
          page,
          resultsPerPage,
          signal
        });
        updateQueryParams();
        setNumberOfResults(data.numberOfResults);
        setSearchData(data.results);
        setHasError(false);
      } catch (err: any) {
        if (axios.isCancel(err)) return;
        if (!silent) {
          setNumberOfResults(0);
          setSearchData([]);
          setHasError(true);
        }
      } finally {
        if (!silent) setSearchIsLoading(false);
      }
    },
    [
      currentChatbot,
      timezoneParsedFilterConfig,
      page,
      resultsPerPage,
      sortDirection,
      sortColumn,
      getAbortSignal
    ]
  );

  const handleFetchConversations = useCallback(
    (): Promise<void> => fetchConversations(),
    [fetchConversations]
  );

  useEffect(() => {
    handleFetchConversations();
  }, [handleFetchConversations]);

  useEffect(() => {
    setFilterConfig((prev) => ({
      ...prev,
      q: query
    }));
    setPage(0);
  }, [query]);

  useEffect(() => {
    setFilterConfig((prev) => ({
      ...prev,
      includeTest
    }));
    setPage(0);
  }, [includeTest]);

  useEffect(() => {
    setFilterConfig((prev) => ({
      ...prev,
      issueReference
    }));
    setPage(0);
  }, [issueReference]);

  const handleSetFilterConfig = (
    newStartOfRange: dayjs.Dayjs | null,
    newEndOfRange: dayjs.Dayjs | null,
    clearSearch?: boolean
  ): void => {
    const newStartTime = newStartOfRange
      ? newStartOfRange.format('YYYY-MM-DDTHH:mm')
      : dateRange[0].format('YYYY-MM-DDTHH:mm');
    const newEndTime = newEndOfRange
      ? newEndOfRange.format('YYYY-MM-DDTHH:mm')
      : dateRange[1].format('YYYY-MM-DDTHH:mm');
    const newQuery = clearSearch ? '' : query;

    if (
      filterConfig.firstMessageStartTime === newStartTime &&
      filterConfig.firstMessageEndTime === newEndTime &&
      filterConfig.q === newQuery
    ) {
      return;
    }

    setFilterConfig((prev) => ({
      ...prev,
      q: newQuery,
      firstMessageStartTime: newStartTime,
      firstMessageEndTime: newEndTime
    }));
    if (clearSearch) {
      setQuery('');
    }
    setPage(0);
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [previousUrl, setPreviousUrl] = useState<string | undefined>();
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
  const [hasExportError, setHasExportError] = useState<boolean>(false);
  const [openExportSnackbar, setOpenExportSnackbar] = useState(false);
  const [sidebarMaxWidth, setSidebarMaxWidth] = useState(
    Math.floor(window.innerWidth * 0.5)
  );

  useEffect(() => {
    const handleResize = (): void => {
      setSidebarMaxWidth(Math.floor(window.innerWidth * 0.5));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleRowClick = (conversation: ConversationResponse): void => {
    const main = document.getElementsByTagName('main')[0];
    const { scrollTop } = main;

    setSearchParams((params) => {
      params.set('search', query);
      params.set('issueReference', issueReference ?? '');
      params.delete('conversationId');
      params.set('firstMessageStartTime', firstMessageStartTime as string);
      params.set('firstMessageEndTime', firstMessageEndTime as string);
      params.set('sortColumn', sortColumn);
      params.set('sortDirection', sortDirection ?? '');
      params.set('page', page.toString());
      params.set('scrollY', Math.floor(scrollTop).toString());
      return params;
    });

    const prev = `${globalThis.location.pathname}?${searchParams.toString()}`;

    setSelectedChatId(Number(conversation.id));
    setSidebarOpen(true);
    setPreviousUrl(prev);
  };

  const handleCloseExportSnackbar = (): void => {
    setHasExportError(false);
    setOpenExportSnackbar(false);
  };

  const reviewedCountChangedRef = useRef(false);

  const handleCloseSidebar = (): void => {
    setSidebarOpen(false);
    if (reviewedCountChangedRef.current) {
      reviewedCountChangedRef.current = false;
      fetchConversations({ silent: true });
    }
  };

  const handleReviewedCountChange = useCallback(
    (reviewedConversationId: number, count: number): void => {
      reviewedCountChangedRef.current = true;
      setSearchData((prev) =>
        prev?.map((row) =>
          Number(row.id) === reviewedConversationId
            ? { ...row, reviewedMessageCount: count }
            : row
        )
      );
    },
    []
  );

  const handleExport = (exportFileType: ExportFileType): void => {
    if (currentChatbot) {
      AnalyticsService.exportMessageHistory(
        currentChatbot.id,
        timezoneParsedFilterConfig,
        exportFileType
      ).catch((err) => {
        if (err.name === 'CanceledError') return;
        setHasExportError(true);
        setOpenExportSnackbar(true);
      });
    }
  };

  const handleExportMissedUtteranceReport = (): void => {
    if (!currentChatbot) return;
    AnalyticsService.exportMissedUtteranceReport(
      currentChatbot.id,
      dateRange[0].format('YYYY-MM-DD'),
      dateRange[1].format('YYYY-MM-DD')
    ).catch((err) => {
      if (err.name === 'CanceledError') return;
      setHasExportError(true);
      setOpenExportSnackbar(true);
    });
  };

  const handleCopyConversationId = (copiedConversationId: string): void => {
    navigator.clipboard.writeText(copiedConversationId);
  };

  const summaryColClass = 'w-[47%] xl:w-[44%]';
  const startTimeColClass = 'w-[13%]';
  const reviewedColClass = 'w-[10%]';
  const healthColClass = 'w-[10%] xl:w-[13%]';
  const flaggedColClass = 'w-[10%] pr-8';
  const issuesColClass = 'w-[12%]';
  const actionsColClass = 'w-[10%] text-center hidden xl:table-cell';

  const conversationHeaders: HeaderDefinition[] = [
    {
      key: 'chatSummary',
      label: 'Summary',
      sortable: false,
      className: summaryColClass
    },
    {
      key: 'firstMessageTime',
      label: 'Start time',
      sortable: true,
      className: startTimeColClass
    },
    {
      key: 'reviewedMessageCount',
      label: 'Reviewed',
      sortable: true,
      className: reviewedColClass
    },
    {
      key: 'health',
      label: 'Health score',
      sortable: true,
      className: healthColClass
    },
    {
      key: 'flagged',
      label: 'Flagged',
      sortable: false,
      className: flaggedColClass
    },
    ...(isJapetoAdmin
      ? [
          {
            key: 'issues',
            label: 'Issues',
            sortable: false,
            className: issuesColClass
          }
        ]
      : []),
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      className: actionsColClass
    }
  ];

  const renderConversationRow = (
    conversation: ConversationResponse
  ): React.ReactElement => {
    return (
      <>
        <TableCell className={summaryColClass}>
          <div className='flex flex-col gap-1 min-w-0'>
            <div className='flex items-center gap-2 min-w-0'>
              <span className='block truncate' title={conversation.chatSummary}>
                {conversation.chatSummary}
              </span>
              {conversation.isTest && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TestTubeDiagonal
                      size={16}
                      className='text-purple-500 shrink-0'
                    />
                  </TooltipTrigger>
                  <TooltipContent>Test conversation</TooltipContent>
                </Tooltip>
              )}
            </div>
            {conversation.tags && conversation.tags.length > 0 && (
              <TopicsContainer tags={conversation.tags} />
            )}
          </div>
        </TableCell>
        <TableCell className={startTimeColClass}>
          {(() => {
            const formatted = formatDate(
              conversation.firstMessageTime as unknown as string
            );
            const [date, time] = formatted.split(' ');
            return (
              <div className='flex flex-col xl:flex-row xl:gap-1'>
                <span>{date}</span>
                <span>{time}</span>
              </div>
            );
          })()}
        </TableCell>
        <TableCell className={reviewedColClass}>
          {(() => {
            const reviewed = conversation.reviewedMessageCount;
            const total = Math.floor(conversation.numberOfMessages / 2);
            if (reviewed === 0) {
              return <span className='text-xs text-muted-foreground'>—</span>;
            }
            const allReviewed = total > 0 && reviewed >= total;
            const badgeClass = allReviewed
              ? 'bg-green-500/10 text-green-700'
              : 'bg-brand-light/10 text-brand-light';
            return (
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${badgeClass}`}
              >
                {reviewed} / {total}
                <span className='hidden xl:inline'>&nbsp;reviewed</span>
              </span>
            );
          })()}
        </TableCell>
        <TableCell className={healthColClass}>
          <HealthScore health={conversation.health} />
        </TableCell>
        <TableCell className={flaggedColClass}>
          {conversation.containsSafeguarding ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton sx={flagStyles.iconButton}>
                  <Flag
                    size={flagStyles.icon.size}
                    fill={flagStyles.color}
                    stroke={flagStyles.color}
                  />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>Flagged</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton sx={flagStyles.iconButton}>
                  <Flag
                    size={flagStyles.icon.size}
                    fill='transparent'
                    stroke='currentColor'
                    style={{ opacity: 0.5 }}
                  />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>Not flagged</TooltipContent>
            </Tooltip>
          )}
        </TableCell>
        {isJapetoAdmin && (
          <TableCell className={issuesColClass}>
            {conversation.issueReferences &&
            conversation.issueReferences.length > 0 ? (
              <TopicsContainer
                tags={conversation.issueReferences}
                variant='issue'
              />
            ) : (
              <span className='text-xs text-muted-foreground'>—</span>
            )}
          </TableCell>
        )}
        <TableCell className={actionsColClass}>
          <ConversationActionsDropdown
            conversation={conversation}
            onCopyConversationId={handleCopyConversationId}
          />
        </TableCell>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>Japeto - Chats</title>
      </Helmet>
      <PageTitleSection title='Chats'>
        <ConversationsDateRangeContainer
          handleSetFilterConfig={handleSetFilterConfig}
          dateRange={dateRange}
          setDateRange={setDateRange}
          daysInRange={daysInRange}
        />
      </PageTitleSection>
      <PageContentContainer>
        <div className='flex justify-between items-center md:flex-row flex-col gap-2 md:gap-4'>
          <div className='flex w-full flex-col gap-2 sm:flex-row sm:items-center md:w-auto md:flex-1'>
            <SearchBar
              setQuery={setQuery}
              searchTermFromState={query}
              placeholder='Search conversations...'
            />
            {isJapetoAdmin && currentChatbot && (
              <div className='w-full sm:w-64'>
                <AsyncSearchSelect<Issue>
                  value={selectedIssue}
                  onSelect={handleIssueFilterSelect}
                  fetchOptions={(issueQuery) =>
                    AnalyticsService.searchIssues(currentChatbot.id, issueQuery)
                  }
                  getOptionKey={(issue) => issue.id}
                  getTriggerLabel={(issue) => issue.reference}
                  placeholder='Filter by issue...'
                  searchPlaceholder='Search issues...'
                  emptyText='No issues found'
                  clearable
                  renderOption={(issue) => (
                    <div className='flex items-baseline gap-2'>
                      <span className='shrink-0 font-medium'>
                        {issue.reference}
                      </span>
                      {(issue.title || issue.description) && (
                        <span className='text-text-secondary min-w-0 text-xs break-words'>
                          {issue.title || issue.description}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>
            )}
          </div>
          <div className='flex items-center gap-4'>
            <div className='flex items-center gap-2'>
              <Switch checked={includeTest} onCheckedChange={setIncludeTest} />
              <span className='text-sm text-muted-foreground'>
                Include test conversations
              </span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ShadcnButton variant='outline' size='sm'>
                  <Download size={16} className='mr-2.5' />
                  Export
                </ShadcnButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuLabel>Export Options</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleExport('csv')}>
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                  Export as Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportMissedUtteranceReport}>
                  Missed utterance report
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <DataTable<ConversationResponse>
          searchData={searchData || null}
          numberOfResults={numberOfResults}
          resultsPerPage={resultsPerPage}
          isLoading={searchIsLoading}
          setResultsPerPage={setResultsPerPage}
          setPage={setPage}
          page={page}
          tableHeaders={conversationHeaders}
          renderRow={renderConversationRow}
          sortColumn={sortColumn || 'firstMessageTime'}
          setSortColumn={
            setSortColumn as React.Dispatch<React.SetStateAction<string>>
          }
          sortDirection={sortDirection || 'DESC'}
          setSortDirection={
            setSortDirection as React.Dispatch<
              React.SetStateAction<'ASC' | 'DESC'>
            >
          }
          onSortChange={() => {
            updateQueryParams();
            setPage(0);
          }}
          handleRowClick={handleRowClick}
          hasError={hasError}
          handleFetch={handleFetchConversations}
          noDataTitle='No chats found'
          noDataMessage="Your chatbot hasn't had any conversations for this date range."
          hasActiveSearch={!!query}
          noSearchResultsTitle='No conversations found'
          noSearchResultsMessage="There aren't any conversations matching your search."
          hideTopPagination
        />
      </PageContentContainer>
      <CustomDrawer
        open={sidebarOpen}
        onClose={handleCloseSidebar}
        resizable
        minWidth={420}
        maxWidth={sidebarMaxWidth}
        defaultWidth={420}
        storageKey='conversation-sidebar-width'
      >
        <ConversationSidebar
          chatId={selectedChatId}
          previousUrl={previousUrl}
          onClose={handleCloseSidebar}
          onReviewedCountChange={handleReviewedCountChange}
        />
      </CustomDrawer>

      {hasExportError && (
        <Snackbar
          open={openExportSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseExportSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseExportSnackbar} severity='error'>
            Failed to export. Please try again later.
          </Alert>
        </Snackbar>
      )}
    </>
  );
}

export default ConversationsPage;
